import React, { useState, useContext, useCallback, useEffect } from 'react';
import cn from 'classnames';
import _ from 'lodash';
import './OfficeOpenSeating.scss';

import { OfficeContext } from './Office';
import { getSmartImage } from '../../helpers/helpers';
import { canManageOffice, formatTimeClean } from '../../helpers/office_helpers';
import { shuffleDeskBookings } from '../../api/office';
import OfficeSpacePopover from './OfficeSpacePopover';

const OfficeOpenSeating = ({ space }) => {
  const ctx = useContext(OfficeContext);
  const { currentUser, focusedUserId, focusedUserSchedule, myBookings, selectedDate, spaces, setSelectedSpace, onBookingChange } = ctx;
  const isAdmin = canManageOffice(currentUser);
  const [showPopover, setShowPopover] = useState(false);
  const [popoverPos, setPopoverPos] = useState(null);
  const popoverRef = React.useRef(null);

  const bookedCount = space.bookings?.length || 0;
  const maxCount = space.max_concurrent_bookings || 50;
  const floorName = space.name || 'Open Seating';
  const myBooking = space.bookings?.find(b => b.User_id === currentUser?.id);
  const isFull = bookedCount >= maxCount && !myBooking;

  const existingDeskBookingToday = myBookings?.find(b => {
    const bIsDesk = b.space?.type === 'desk' || b.space?.type === 'open_seating';
    if (!bIsDesk) return false;
    return b.starts_at && b.starts_at.startsWith(selectedDate);
  });

  const openPopoverFrom = useCallback(el => {
    if (!el) return;
    const rect = el.getBoundingClientRect();
    setPopoverPos({
      position: 'fixed',
      bottom: window.innerHeight - rect.top + 8,
      left: Math.min(rect.left, window.innerWidth - 340)
    });
    setShowPopover(true);
  }, []);

  const availableDesks = spaces.filter(s => s.type === 'desk' && !s.bookings?.length);
  const openRandomDesk = () => setSelectedSpace(_.sample(availableDesks));

  const shuffleDesks = async () => {
    const confirm = window.confirm('Are you sure you want to shuffle all desk assignments for this day?');
    if (!confirm) return;
    try {
      await shuffleDeskBookings({ date: selectedDate, floor_id: space.OfficeFloor_id });
      if (onBookingChange) await onBookingChange();
      window.ALERT.success('Shuffled desks');
    } catch (err) {
      console.error('Failed to shuffle desks:', err);
      window.ALERT.error(err ?? 'There was an error shuffling desks');
    }
  };

  useEffect(() => {
    if (!showPopover) return;
    const handleClickOutside = e => {
      if (popoverRef.current?.contains(e.target)) return;
      setShowPopover(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showPopover]);

  const seats = [];
  for (let i = 0; i < maxCount; i++) {
    const booking = space.bookings?.[i] || null;
    seats.push({ index: i, booking });
  }

  return (
    <div className='office-open-seating-outer'>
      <div className='open-seating-header'>
        <div className='open-seating-info'>
          <span className='open-seating-name'>{floorName}</span>
          <span className='open-seating-count'>
            <strong>{bookedCount}</strong> / {maxCount} seats taken
          </span>
        </div>
        <div className='open-seating-btns'>
          <button
            className={cn('open-seating-book-btn', { active: showPopover })}
            onClick={e => (showPopover ? setShowPopover(false) : openPopoverFrom(e.currentTarget))}
            disabled={isFull && !myBooking}
          >
            {isFull && !myBooking ? 'Full' : myBooking ? 'My Seat' : existingDeskBookingToday ? 'Change Booking' : 'Book Open Seating'}
          </button>
          <button className={cn('open-seating-book-btn', { active: showPopover })} onClick={openRandomDesk} disabled={!availableDesks.length}>
            Book a Random Desk
          </button>
          {isAdmin && (
            <button className={cn('open-seating-book-btn', { active: showPopover })} onClick={shuffleDesks}>
              Shuffle Desks
            </button>
          )}
        </div>
      </div>

      <div className='open-seating-grid'>
        {seats.map(seat => (
          <div
            key={seat.index}
            className={cn('seat-dot', {
              booked: !!seat.booking,
              mine: seat.booking?.User_id === currentUser?.id,
              clickable: !seat.booking && !isFull,
              'user-dimmed': focusedUserId && seat.booking?.User_id !== focusedUserId,
              'user-highlighted': focusedUserId && seat.booking?.User_id === focusedUserId
            })}
            title={seat.booking?.title || seat.booking?.user?.name || `Seat ${seat.index + 1}`}
            onClick={e => openPopoverFrom(e.currentTarget)}
          >
            {seat.booking?.title ? null : seat.booking?.user?.image ? <img src={getSmartImage(seat.booking.user.image)} alt='' className='seat-avatar' /> : null}
            {focusedUserId &&
              seat.booking?.User_id === focusedUserId &&
              (() => {
                const seatBookings = focusedUserSchedule.filter(b => b.OfficeSpace_id === space.id || b.space?.id === space.id);
                if (!seatBookings.length) return null;
                return (
                  <div className='seat-schedule-badge'>
                    {seatBookings.map(b => {
                      const startFmt = formatTimeClean(b.starts_at);
                      const endFmt = formatTimeClean(b.ends_at);
                      return (
                        <span key={b.id} className='badge-time'>
                          {startFmt} – {endFmt}
                        </span>
                      );
                    })}
                  </div>
                );
              })()}
          </div>
        ))}
      </div>

      {showPopover && popoverPos && (
        <div ref={popoverRef}>
          <OfficeSpacePopover space={space} onClose={() => setShowPopover(false)} positionStyle={popoverPos} />
        </div>
      )}
    </div>
  );
};

export default OfficeOpenSeating;
